import React, { useEffect, useState } from "react";
import { Form, Button, Row, Col, Alert } from 'react-bootstrap'
import Spinner from "react-bootstrap/Spinner";
import { useDispatch, useSelector } from "react-redux";
import { clearTwoFactorChallenge, login, logout, verifyOtp } from "./authenticationSlice";
import { Redirect, useLocation } from "react-router-dom";
import { GoogleSsoButton } from "./GoogleSsoButton";

export const LoginForm = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const [userName, setUserName] = useState('');
    const [password, setPassword] = useState('');
    const [isRedirect, setRedirect] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [otpCode, setOtpCode] = useState('');

    // Check for error in URL parameters
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const error = params.get('error');
        const message = params.get('message');

        if (error || message) {
            const errorMessages = {
                'unauthorized_domain': 'Only @gearinc.com email addresses are allowed',
                'user_creation_failed': 'Unable to create your account. Please try again.',
                'account_inactive': 'Your account has been deactivated',
                'sso_failed': message ? decodeURIComponent(message) : 'SSO authentication failed'
            };

            setErrorMessage(errorMessages[error] || message || 'An error occurred during login');

            // Clear the error from URL after displaying
            window.history.replaceState({}, document.title, location.pathname);
        }
    }, [location]);

    // reset login status. Call only once
    useEffect(() => {
        dispatch(logout());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const onUserNameChanged = e => setUserName(e.target.value);
    const onPasswordChanged = e => setPassword(e.target.value);
    const { status, pendingChallenge, twoFactorMessage, twoFactorEmailHint } = useSelector(state => state.authentication);
    const isOtpStep = Boolean(pendingChallenge);
    const canSubmit = [userName, password].every(Boolean) && status === 'idle' && !isOtpStep;
    const canSubmitOtp = Boolean(otpCode) && status === 'idle' && isOtpStep;

    let buttonContent;
    if (status !== 'idle') {
        buttonContent = <Spinner animation="border" />
    } else {
        buttonContent = <span>Sign In</span>
    }
    if (isRedirect) {
        return <Redirect to="/" />
    }

    const handleSubmit = async (e) => {
        if (e) {
            e.preventDefault();
        }
        
        if (canSubmit) {
            try {
                const result = await dispatch(
                    login({
                        user_name: userName,
                        password: password
                    })
                );

                // Check if login was successful
                if (result.type === 'authentication/login/fulfilled') {
                    if (result.payload?.status === 'otp_required') {
                        setErrorMessage('');
                        setOtpCode('');
                    } else {
                        setRedirect(true);
                    }
                } else if (result.type === 'authentication/login/rejected') {
                    // Handle login error
                    const errorMsg = result.payload?.message || result.error?.message || 'Invalid username or password';
                    setErrorMessage(errorMsg);
                }
            } catch (e) {
                console.error('Failed to login: ', e);
                setErrorMessage('An unexpected error occurred. Please try again.');
            }
        }
    };


    const handleOtpSubmit = async (e) => {
        if (e) {
            e.preventDefault();
        }

        if (!isOtpStep || !canSubmitOtp) {
            return;
        }

        try {
            const result = await dispatch(
                verifyOtp({
                    challenge_token: pendingChallenge,
                    otp_code: otpCode
                })
            );

            if (result.type === 'authentication/verifyOtp/fulfilled') {
                setRedirect(true);
            } else if (result.type === 'authentication/verifyOtp/rejected') {
                const errorMsg = result.payload?.message || result.error?.message || 'Unable to verify the code';
                setErrorMessage(errorMsg);
            }
        } catch (e) {
            console.error('Failed to verify OTP: ', e);
            setErrorMessage('An unexpected error occurred. Please try again.');
        }
    };

    const handleUseDifferentAccount = () => {
        dispatch(clearTwoFactorChallenge());
        setOtpCode('');
        setPassword('');
    };

    return (
        <Row className="justify-content-center mt-5">
            <Col xs={3}>
                {/* Display error message if exists */}
                {errorMessage && (
                    <Alert
                        variant="danger"
                        dismissible
                        onClose={() => setErrorMessage('')}
                        className="mb-3"
                    >
                        <Alert.Heading>Authentication Error</Alert.Heading>
                        <p className="mb-0">{errorMessage}</p>
                    </Alert>
                )}

                {isOtpStep && twoFactorMessage && (
                    <Alert
                        variant="info"
                        className="mb-3"
                    >
                        <Alert.Heading>Check your email</Alert.Heading>
                        <p className="mb-0">
                            {twoFactorMessage} {twoFactorEmailHint ? `(${twoFactorEmailHint})` : ''}
                        </p>
                    </Alert>
                )}

                {/* Traditional Login Form */}
                {!isOtpStep && (
                    <Form onSubmit={handleSubmit}>
                        <Form.Group>
                            <Form.Label className="text-white">User name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter user name"
                                value={userName}
                                onChange={onUserNameChanged}
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label className="text-white">Password</Form.Label>
                            <Form.Control
                                type="password"
                                placeholder="Password"
                                value={password}
                                onChange={onPasswordChanged}
                            />
                        </Form.Group>
                        <Button 
                            variant="primary" 
                            type="submit" 
                            disabled={!canSubmit} 
                            onClick={handleSubmit}
                            className="w-100 mt-3"
                        >
                            {buttonContent}
                        </Button>
                    </Form>
                )}

                {isOtpStep && (
                    <Form onSubmit={handleOtpSubmit}>
                        <Form.Group>
                            <Form.Label className="text-white">Verification code</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter the 6-digit code"
                                value={otpCode}
                                onChange={(e) => setOtpCode(e.target.value)}
                                maxLength={6}
                            />
                        </Form.Group>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={!canSubmitOtp}
                            onClick={handleOtpSubmit}
                            className="w-100 mt-3"
                        >
                            {status !== 'idle' ? <Spinner animation="border" /> : <span>Verify Code</span>}
                        </Button>
                        <Button
                            variant="link"
                            type="button"
                            className="w-100 mt-2 text-white"
                            onClick={handleUseDifferentAccount}
                        >
                            Use a different account
                        </Button>
                    </Form>
                )}

                <div className="text-center mb-3">
                    <span className="text-white"></span>
                </div>
                <hr />
                <div className="mb-4">
                    <GoogleSsoButton />
                </div>
            </Col>
        </Row>
    )
};
